// useSubscription Hook - Manage user subscription state and feature access
import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import subscriptionService, { Subscription, FREE_TIER } from '../services/subscriptionService';

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  canAccessFeature: (feature: string) => boolean;
  startTrial: () => Promise<void>;
  upgradeToPremium: () => Promise<void>;
  downgradeToFree: () => Promise<void>;
  isTrialActive: () => boolean;
  getRemainingTrialDays: () => number;
  refreshSubscription: () => Promise<void>;
}

export const useSubscription = (user: User | null): UseSubscriptionReturn => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user subscription on mount or user change
  useEffect(() => {
    if (user) {
      // 🚀 SPEED OPTIMIZATION: Load subscription in background with delay
      // This prevents blocking the initial login flow
      const timer = setTimeout(() => {
        loadSubscription();
      }, 1000); // Delay 1 second to prioritize main UI
      
      return () => clearTimeout(timer);
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [user]);

  const loadSubscription = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // 🚀 SPEED OPTIMIZATION: Try cache first
      const cachedSub = localStorage.getItem(`subscription_${user.uid}`);
      if (cachedSub) {
        try {
          const parsed = JSON.parse(cachedSub);
          setSubscription(parsed);
          setLoading(false);
          // Continue loading fresh data in background
        } catch (e) {
          console.error('Error parsing cached subscription:', e);
        }
      }
      
      const userSubscription = await subscriptionService.getUserSubscription(user.uid);
      setSubscription(userSubscription);
      
      // Cache subscription
      try {
        localStorage.setItem(`subscription_${user.uid}`, JSON.stringify(userSubscription));
      } catch (e) {
        console.error('Error caching subscription:', e);
      }
      
      // Check if trial has expired (in background)
      if (userSubscription && userSubscription.tier === 'trial' && !subscriptionService.isTrialActive(userSubscription)) {
        console.log('🕐 Trial expired, downgrading to free');
        setTimeout(() => downgradeToFree(), 0);
      }
      
    } catch (err: any) {
      console.error('Error loading subscription:', err);
      setError(err.message);
      setSubscription(FREE_TIER); // Fallback to free tier
    } finally {
      setLoading(false);
    }
  };

  const canAccessFeature = (feature: string): boolean => {
    if (!subscription) return false;
    return subscriptionService.canAccessFeature(subscription, feature);
  };

  const startTrial = async (): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      const trialSubscription = await subscriptionService.startTrial(user.uid);
      setSubscription(trialSubscription);
      console.log('🎉 Trial started successfully!');
    } catch (err: any) {
      console.error('Error starting trial:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const upgradeToPremium = async (): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      const premiumSubscription = await subscriptionService.upgradeToPremium(user.uid);
      setSubscription(premiumSubscription);
      console.log('🚀 Upgraded to premium successfully!');
    } catch (err: any) {
      console.error('Error upgrading to premium:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const downgradeToFree = async (): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      setLoading(true);
      const freeSubscription = await subscriptionService.downgradeToFree(user.uid);
      setSubscription(freeSubscription);
      console.log('📉 Downgraded to free tier');
    } catch (err: any) {
      console.error('Error downgrading to free:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const isTrialActive = (): boolean => {
    if (!subscription) return false;
    return subscriptionService.isTrialActive(subscription);
  };

  const getRemainingTrialDays = (): number => {
    if (!subscription) return 0;
    return subscriptionService.getRemainingTrialDays(subscription);
  };

  const refreshSubscription = async (): Promise<void> => {
    await loadSubscription();
  };

  return {
    subscription,
    loading,
    error,
    canAccessFeature,
    startTrial,
    upgradeToPremium,
    downgradeToFree,
    isTrialActive,
    getRemainingTrialDays,
    refreshSubscription
  };
};













